import { trackLanguageDropdownOpen } from './analytics/trackLanguageDropdownOpen';
import ensureGTranslate from './utils/ensureGTranslate.js';
import updateLangLink from './utils/updateLangLink.js';
/**
 * A custom element for enhancing the language button to open a dialog
 * with language selection options from the mobile menu
 *
 * @module iabbb-language-selector
 */

class DesktopLanguageSelector extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) {
      return;
    }

    this.button.addEventListener('click', this.openDialog);

    this.dialogRoot = this.closest('iabbb-header') || document.body;
    const dialog = this.dialogRoot.querySelector('.language-dialog');
    if (dialog) {
      this.setupDialog(dialog);
    } else {
      // Wait for dialog to appear in DOM
      this.dialogObserver = new MutationObserver(() => {
        const dialog = this.dialogRoot.querySelector('.language-dialog');
        if (dialog) {
          this.dialogObserver.disconnect();
          this.setupDialog(dialog);
        }
      });
      this.dialogObserver.observe(this.dialogRoot, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.openDialog);
    this.dialogObserver?.disconnect();
    this.languageLinksObserver?.disconnect();
    this.dialog?.removeEventListener('click', this.handleDialogClick);
    this.dialog?.removeEventListener('cancel', this.handleDialogCancel);
    this.dialog?.removeEventListener('close', this.handleDialogClose);
  }

  setupDialog = (dialog) => {
    this.dialog = dialog;
    this.setupCloseHandlers();
    this.setupLanguageLinks();

    // GTranslate may finish loading after this component is connected.
    this.languageLinksObserver = new MutationObserver(this.setupLanguageLinks);
    this.languageLinksObserver.observe(dialog, { childList: true, subtree: true });
  };

  openDialog = () => {
    if (!this.dialog || this.dialog.open) {
      return;
    }

    //Analytic: Desktop - header_language_dropdown_open
    trackLanguageDropdownOpen();

    // Position and show dialog
    this.positionDialog();
    this.dialog.showModal();
    this.button.setAttribute('aria-expanded', 'true');
  };

  closeDialog = () => {
    if (this.dialog?.open) {
      this.dialog.close();
    }

    this.resetDialogTrigger();
  };

  setupLanguageLinks = () => {
    ensureGTranslate();

    const languageLinks = this.dialog.querySelectorAll('.gtranslate_wrapper a');

    for (const link of languageLinks) {
      updateLangLink(link, this.closeDialog);
    }
  };

  positionDialog = () => {
    const buttonRect = this.button.getBoundingClientRect();

    // Position dialog below the button
    this.dialog.style.position = 'fixed';
    this.dialog.style.left = `${buttonRect.left}px`;
    this.dialog.style.top = `${buttonRect.bottom + 8}px`;
    this.dialog.style.margin = '0';
  };

  handleDialogClick = (event) => {
    const dialogDimensions = this.dialog.getBoundingClientRect();
    if (
      event.clientX < dialogDimensions.left ||
      event.clientX > dialogDimensions.right ||
      event.clientY < dialogDimensions.top ||
      event.clientY > dialogDimensions.bottom
    ) {
      this.closeDialog();
    }
  };

  handleDialogCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  handleDialogClose = () => {
    // Ignore a delayed close event if the dialog has already been reopened.
    if (!this.dialog.open) {
      this.resetDialogTrigger();
    }
  };

  resetDialogTrigger = () => {
    this.button.setAttribute('aria-expanded', 'false');
    this.button.focus();
  };

  setupCloseHandlers = () => {
    // A click on a modal dialog's backdrop is dispatched to the dialog itself.
    this.dialog.addEventListener('click', this.handleDialogClick);
    this.dialog.addEventListener('cancel', this.handleDialogCancel);
    this.dialog.addEventListener('close', this.handleDialogClose);
  };
}

customElements.define('iabbb-language-selector', DesktopLanguageSelector);
